/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React from 'react';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Grid from '@material-ui/core/Grid';
import makeStyles from '@material-ui/styles/makeStyles';
import { DashboardCardStatus } from './DashboardCardStatus';
import { DashboardCardHeader } from './DashboardCardHeader';
import { DashboardCardChart } from './DashboardCardChart';
import { SubmissionStatus } from '../../../models/SubmissionStatus';
import { useDashboardCard } from '../../../redux/features/dashboard/useDashboardCard';
import useDraftManagement from '../../draftManagement/useDraftManagement';
import DraftManagementDialogs from '../../draftManagement/components/DraftManagementDialogs';

const Procedures = Object.freeze({
  NATPAT: 'natpat',
  SPC: 'spc',
  EPVL: 'epvl',
  UM: 'um',
});

const getProcedureByLabel = (label) => {
  const procedureTxtArr = label?.split('.');
  if (procedureTxtArr?.length > 0) {
    return procedureTxtArr[1]?.toLowerCase();
  }
  return null;
};

const useStyles = makeStyles(theme => ({
  root: {
    position: 'relative',
  },
  cardContent: {
    padding: '0',
    margin: '0 1.2rem 0 1.2rem',
  },
  gridContainer: {
    [theme.breakpoints.up('md')]: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
    },
    [theme.breakpoints.up('xl2')]: {
      gridTemplateColumns: '.5fr 1fr',
    },
  },
  cardsContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    justifyContent: 'center',
  },
  highlight: {
    background: ({ label }) => {
      const activeProcedure = getProcedureByLabel(label);
      let color =
        label === 'NP Submissions'
          ? theme.palette.submissionOverviewCardHighlight
          : theme.palette.defaultOverviewCardHighlight;
      if (!theme.palette?.procedures) {
        return color;
      }
      switch (activeProcedure) {
        case Procedures.NATPAT:
          color = theme.palette?.procedures.natpat;
          break;
        case Procedures.SPC:
          color = theme.palette?.procedures.spc;
          break;
        case Procedures.EPVL:
          color = theme.palette?.procedures.epvl;
          break;
        case Procedures.UM:
          color = theme.palette?.procedures.um;
          break;
        default:
          break;
      }
      return color || theme.palette.submissionOverviewCardHighlight;
    },
    width: '.5rem',
    height: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
  },
  buttonsContainer: {
    display: 'grid',
    gridGap: '1rem',
    gridTemplateColumns: '1fr 1fr',
  },
  createButton: {
    width: '100%',
  },
}));

export function DashboardCard({ procedureId }) {
  const {
    total,
    label,
    dashboardLabel,
    procedureData,
    navigationUrl,
    saEnabled,
    disableExplicitSaCreation,
    hideInitialSubmissions,
  } = useDashboardCard({
    procedureId,
  });
  const draftManagementHook = useDraftManagement(procedureId);
  const classes = useStyles({ label });
  if (!procedureId) return <></>;

  return (
    <>
      <Card className={classes.root}>
        <DashboardCardHeader label={dashboardLabel || label} navigationUrl={navigationUrl} />
        <CardContent className={classes.cardContent}>
          <span className={classes.highlight} aria-hidden="true" />
          <Grid container classes={{ root: classes.gridContainer }}>
            <Grid item>
              <DashboardCardChart data={procedureData} total={total} label={label} />
            </Grid>
            <Grid item classes={{ root: classes.cardsContainer }}>
              {procedureData.map((status) => {
                const statusI18nKey = SubmissionStatus[status.statusName]?.i18nKey;
                return (
                  <DashboardCardStatus
                    id={`${label}-${statusI18nKey}`}
                    key={status.statusName}
                    total={status.total}
                    status={status.statusName}
                    label={statusI18nKey}
                    navigationUrl={navigationUrl}
                  />
                );
              })}
            </Grid>
          </Grid>
          <Grid container classes={{ root: classes.buttonsContainer }}>
            <Grid item>
              {saEnabled && !disableExplicitSaCreation && (
                <draftManagementHook.CreateSAButton id={procedureId} className={classes.createButton} />
              )}
            </Grid>

            <Grid item>
              {!hideInitialSubmissions && (
                <div className={classes.createButton}>{draftManagementHook.createButton}</div>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      <DraftManagementDialogs procedureId={procedureId} {...draftManagementHook} />
    </>
  );
}
